import React, { ChangeEvent } from "react";
import useResumeStore from "../../../store/useResumeStore";
import { Plus, X } from "lucide-react";

interface EducationEntry {
  id: string;
  school: string;
  degree: string;
  field: string;
  gpa: string;
  startDate: string;
  endDate: string;
}

const EducationInfo: React.FC = () => {
  const {
    education,
    addEducation,
    updateEducation,
    removeEducation,
  } = useResumeStore();

  const handleInputChange = (
    id: string,
    field: keyof EducationEntry,
    value: string
  ) => {
    updateEducation(id, field, value);
  };

  if (education.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-16">
        <img
          src="https://cdn-icons-png.flaticon.com/512/3135/3135789.png"
          alt="Empty Education"
          className="w-40 h-40 mb-6 opacity-70"
        />
        <h2 className="text-2xl font-semibold text-white mb-2">No Education Added</h2>
        <p className="text-gray-400 max-w-md mb-6 text-sm">
          Listing your educational background helps recruiters understand your academic journey and achievements. It's a strong foundation for your resume.
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => addEducation()}
            className="flex items-center gap-2 px-5 py-2 bg-[#79e708] text-black font-semibold rounded-lg hover:bg-[#6bc906] transition-colors duration-200 shadow-lg"
          >
            <Plus size={16} />
            Add Education
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-white flex items-center">
          <span className="w-2 h-2 bg-[#79e708] rounded-full mr-3"></span>
          Education
        </h2>
        <button
          onClick={() => addEducation}
          className="flex items-center gap-2 px-4 py-2 bg-[#79e708] text-black font-medium rounded-lg hover:bg-[#6bc906] transition-colors duration-200 shadow-lg"
        >
          <Plus size={16} />
          Add
        </button>
      </div>
      <div className="space-y-4">
        {education.map((edu: EducationEntry) => (
          <div
            key={edu.id}
            className="border border-gray-700 rounded-lg p-4 relative bg-gray-800 hover:bg-gray-750 transition-colors duration-200"
          >
            <button
              onClick={() => removeEducation(edu.id)}
              className="absolute top-3 right-3 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-full p-1 transition-colors duration-200"
            >
              <X size={16} />
            </button>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="School/University"
                value={edu.school}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(edu.id, "school", e.target.value)
                }
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#79e708] focus:border-transparent transition-all duration-200 hover:border-gray-500"
              />
              <input
                type="text"
                placeholder="Degree"
                value={edu.degree}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(edu.id, "degree", e.target.value)
                }
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#79e708] focus:border-transparent transition-all duration-200 hover:border-gray-500"
              />
              <input
                type="text"
                placeholder="Field of Study"
                value={edu.field}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(edu.id, "field", e.target.value)
                }
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#79e708] focus:border-transparent transition-all duration-200 hover:border-gray-500"
              />
              <input
                type="text"
                placeholder="GPA (optional)"
                value={edu.gpa}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(edu.id, "gpa", e.target.value)
                }
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#79e708] focus:border-transparent transition-all duration-200 hover:border-gray-500"
              />
              <input
                type="date"
                placeholder="Start Date"
                value={edu.startDate}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(edu.id, "startDate", e.target.value)
                }
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#79e708] focus:border-transparent transition-all duration-200 hover:border-gray-500"
              />
              <input
                type="date"
                placeholder="End Date"
                value={edu.endDate}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(edu.id, "endDate", e.target.value)
                }
                className="w-full px-4 py-3 bg-black border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#79e708] focus:border-transparent transition-all duration-200 hover:border-gray-500"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EducationInfo;
